<template>
  <b-card class="card__learning-unit card__mc h-100" no-body>
    <b-card-header header-class="lu-header">
      <base-presigned-image
        class="lu__thumbnail lu__thumbnail--img lu-image"
        :file-id="imageFileId"
        :default-src="
          imageVideoThumbnailSrc ? imageVideoThumbnailSrc : require('@/assets/default-lu-cover.jpg')
        "
        public
      >
      </base-presigned-image>
      <b-badge
        v-if="learningUnit && learningUnit.is_awarded"
        class="badge-enrolled tag__enrolled"
        variant="success"
      >
        ACHIEVED
      </b-badge>
    </b-card-header>

    <b-card-body class="mc-card-body">
      <McExploreDetail
        v-if="learningUnit"
        class="mb-0"
        :mc="learningUnit"
        :limit-description-line="descriptionLineLimit"
      >
      </McExploreDetail>
    </b-card-body>

    <template #footer footer-class="mc-card-footer">
      <div v-if="learningUnit && learningUnit.is_applied">
        <b-button
          v-if="isAppliedLearnVerifyMode"
          class="w-100 mb-2 btn-go-to-learning"
          variant="primary"
          :to="getFeedRoute()"
          target="_blank"
        >
          Go to learning
        </b-button>

        <b-button
          class="w-100 btn-verify-or-badge"
          variant="outline-primary"
          :to="getLearningUnitRoute()"
          target="_blank"
        >
          {{ learningUnit.is_awarded ? 'Go to badge collection' : 'Verify competency' }}
        </b-button>
      </div>
      <div v-else-if="isRoleWarningVisible">
        <p class="text-danger">
          *Cannot access the MCs due to your role as a {{ isOwner ? 'designer' : 'assessor' }}.
        </p>
      </div>
      <div v-else>
        <hr v-if="isOpenToApply" class="divider" />
        <div class="card-lu__footer">
          <div v-show="isOpenToApply" class="text__mode_displayer">
            <span v-if="learningUnit.open_apply_learn" class="lu-footer text-mc-mode text-black-50"
              >VERIFY / LEARN</span
            >
            <span
              v-if="learningUnit.open_apply_verify && !learningUnit.open_apply_learn"
              class="lu-footer text-mc-mode text-black-50"
            >
              VERIFY
            </span>
          </div>
          <div v-show="isOpenToApply" class="text__price_displayer text-black">
            <span
              v-if="learningUnit.price_display && learningUnit.price_display !== 0 && isOpenToApply"
              class="align-middle lu-footer text-mc-price"
              >Start at {{ formatPrice(learningUnit.price_display) }} THB
            </span>
            <span
              v-else-if="learningUnit.price_display === 0 && isOpenToApply"
              class="align-middle lu-footer text-mc-price"
              >FREE</span
            >
          </div>
        </div>
      </div>
    </template>
  </b-card>
</template>

<script>
import McExploreDetail from './McExploreDetail.vue';
import BasePresignedImage from '@/components/base-components/BasePresignedImage.vue';

export default {
  components: {
    McExploreDetail,
    BasePresignedImage
  },

  props: {
    learningUnit: {
      type: Object,
      default: null
    },
    thumbnail: {
      type: String,
      default: '@/assets/default-lu-cover.jpg'
    }
  },
  computed: {
    isOpenToApply() {
      return this.learningUnit.open_apply_verify || this.learningUnit.open_apply_learn;
    },
    imageSrc() {
      let src = null;

      if (this.learningUnit.media_detail && this.learningUnit.media_detail[0]) {
        src = this.learningUnit.media_detail[0].url;
      } else if (this.learningUnit.media_detail_vimeo?.thumbnail_link) {
        src = this.learningUnit.media_detail_vimeo.thumbnail_link;
      }

      return src;
    },
    imageFileId() {
      let fileId = null;

      if (this.learningUnit.media_detail) {
        if (
          Array.isArray(this.learningUnit.media_detail) &&
          this.learningUnit.media_detail.length > 0
        ) {
          fileId = this.learningUnit.media_detail[0].id;
        } else {
          fileId = this.learningUnit.media_detail.id;
        }
      }

      return fileId;
    },
    imageVideoThumbnailSrc() {
      let src = null;

      if (this.learningUnit.media_detail_vimeo?.thumbnail_link) {
        src = this.learningUnit.media_detail_vimeo.thumbnail_link;
      }

      return src;
    },
    isOwner() {
      return this.$currentUser?.isOwnerOf(this.learningUnit?.id);
    },
    isAssessor() {
      return this.$currentUser?.isAssessorOf(this.learningUnit?.id);
    },
    isRoleWarningVisible() {
      return (this.isOwner || this.isAssessor) && this.$route?.name === 'stack-detail';
    },
    descriptionLineLimit() {
      return this.learningUnit?.is_applied && this.learningUnit?.open_apply_learn ? 3 : 5;
    },
    isAppliedLearnVerifyMode() {
      return this.learningUnit?.applications?.[0]?.mode === 'LEARN_VERIFY';
    }
  },

  methods: {
    formatPrice(number) {
      return Intl.NumberFormat('en-US', {
        currency: 'USD'
      }).format(number);
    },
    getLearningUnitRoute() {
      // If user has awarded, go to badge collection
      if (this.learningUnit.is_awarded) {
        return {
          name: 'badge-collection',
          query: { learningUnitId: this.learningUnit.id }
        };
      }
      // If user has applied but not awarded, go to method components
      else if (this.learningUnit.is_applied) {
        return {
          name: 'learning-unit/method-components',
          params: { learningUnitId: this.learningUnit.id }
        };
      }
    },
    getFeedRoute() {
      return {
        name: 'learning-unit/feed',
        params: {
          learningUnitId: this.learningUnit.id,
          sectionId: this.learningUnit.section_id
        },
        query: {
          tab: 'posts'
        }
      };
    }
  }
};
</script>

<style lang="scss" scoped>
.card__learning-unit {
  box-shadow: none;
  border: 1px solid #d1d2db;
  border-radius: 10.4px;
  .card-header:first-child {
    border-radius: 9px 9px 0px 0px;
  }

  .lu__thumbnail--img {
    width: 100%;
    height: 100%;
  }
}
.mc-card-body {
  min-height: 200px;
  padding: 20px 20px 0 20px;
}
.card-lu__footer {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.divider {
  margin-top: 0px;
  margin-bottom: 16px;
}

.lu-footer {
  font-size: 16px;
  text-align: center;
}
.badge-enrolled {
  position: absolute;
  right: 10px;
  top: 10px;
  z-index: 1;
  border-radius: 10px;
  letter-spacing: 0;
}

.lu-header {
  aspect-ratio: 1.69;
  position: relative;
  padding: 0;
}
.card-footer {
  padding: 16px 20px;
}

@media (max-width: 765px) {
  .text-mc-mode {
    font-size: 10px;
  }
  .text-mc-price {
    font-size: 10px;
  }
  .mc-card-body {
    min-height: 112px;
  }
  .mc-card-body,
  .card-footer {
    padding: 8px;
  }
  .divider {
    margin-top: 0px;
    margin-bottom: 8px;
  }

  .btn-verify-or-badge,
  .btn-go-to-learning {
    padding: 0.5rem;
    font-size: 12px;
  }
}
</style>
